import { EM_DASH } from '../lib/typography';
import { Component, createRef } from 'preact';
import {
  GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, BG_ATMOSPHERE, VIGNETTE,
  KnotOrnament, HeadingWatermark, GLASS_PANEL_SHEER_STYLE, INPUT_STYLE,
} from '../lib/medievalTheme';
import { CloseButton } from './ui/navButtons';
import { CoinIcon } from './ui/icons';
import CardInspector from './CardInspector';
import DeckCardTile from './DeckCardTile';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import DiscordHandlePill from './ui/DiscordHandlePill';
import PlayerTitle from './PlayerTitle';
import PlayerBadgeStrip from './PlayerBadgeStrip';
import VirtualList from './VirtualList';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import { playUI, UI } from '../utils/arena/uiSounds';
import { getCanvasScale, onCanvasScaleChange } from '../utils/uiScale';
import {
  getCurrentRankedSeason,
  getRankedSeasonLeaderboard,
  getRankedSeasonSelf,
} from '../utils/arena/rankedSeasonApi';
import { loadCurrentSeason } from '../utils/arena/seasonApi';
import { resolvePrinting } from '../utils/sorcery/foil';
import { k, t } from '../localization/t';

// Extract the integer cycle number from the Arcane Trials season id
// (which is a "cycle-N" string). Falls back to null if the shape isn't
// what we expect, in which case the caller renders the season label
// without a number rather than displaying "cycle-NaN".
function cycleNumberFromTrials(seasonId) {
  if (typeof seasonId !== 'string') return null;
  const m = seasonId.match(/^cycle-(\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

// Default scoring + reward fallbacks for when the
// /season-ranked/current endpoint isn't available yet (old beta build).
// Bounds always come from the Arcane Trials season, so the countdown
// stays in lockstep with the trials screen regardless of whether the
// ranked-season endpoint has been deployed.
const FALLBACK_RANKED_META = {
  minMatches: 10,
  rewards: { coins: [500, 250, 150] },
  scoring: { basePoints: 10, minPoints: 1, maxPoints: 40 },
};

// Merge the ranked-season endpoint response with the Arcane Trials
// season payload into the single shape the rest of the component
// consumes. Trials wins on bounds + cycle number (single source of
// truth); ranked wins on rewards + scoring + minMatches. Either side
// is allowed to be null. The helper fills in plausible defaults so
// the UI still renders during partial outages.
function buildMergedSeason(rankedMeta, trialsResp) {
  const trials = trialsResp?.season || null;
  const cycleNum = rankedMeta?.seasonId
    ?? cycleNumberFromTrials(trials?.seasonId)
    ?? null;
  const startsAt = trials?.startsAt || rankedMeta?.startsAt || null;
  const endsAt = trials?.endsAt || rankedMeta?.endsAt || null;
  return {
    seasonId: cycleNum,
    startsAt,
    endsAt,
    minMatches: rankedMeta?.minMatches ?? FALLBACK_RANKED_META.minMatches,
    rewards: rankedMeta?.rewards ?? FALLBACK_RANKED_META.rewards,
    scoring: rankedMeta?.scoring ?? FALLBACK_RANKED_META.scoring,
    // The season's four prize cards (Ordinary → Unique slots). Absent
    // on old server builds, in which case the prizes panel hides.
    prizes: rankedMeta?.prizes ?? [],
  };
}

const RANK_MEDAL_COLORS = {
  1: ['#fff2bf', '#f0c84a', '#7d5a16'],
  2: ['#f6f6f6', '#cfcfcf', '#7a7a7a'],
  3: ['#f0c08a', '#c3854a', '#5e3614'],
};

// Same tier palette the leaderboard rows + avatar rings tint against.
// Mirrored from ArenaHub's leaderboard so the visual language stays
// consistent between the all-time ladder and the seasonal ladder.
const TIER_HEX = {
  apprentice: '#a8a29e',
  journeyman: '#d4d4d8',
  adept: '#38bdf8',
  expert: '#a78bfa',
  master: '#fbbf24',
  grandmaster: '#f87171',
};

// Top-3 rows get a faint medal-tinted wash so the leaderboard reads
// like a podium even when scrolled below the fold. Lower alpha than
// the medallion itself so the names stay legible.
const TOP_ROW_TINT = {
  1: 'linear-gradient(90deg, rgba(240,200,74,0.10) 0%, rgba(240,200,74,0.02) 60%, transparent 100%)',
  2: 'linear-gradient(90deg, rgba(207,207,207,0.08) 0%, rgba(207,207,207,0.02) 60%, transparent 100%)',
  3: 'linear-gradient(90deg, rgba(195,133,74,0.08) 0%, rgba(195,133,74,0.02) 60%, transparent 100%)',
};

const PAGE_SIZE = 50;

export default class SeasonRankingScreen extends Component {
  state = {
    loading: true,
    error: null,
    season: null,
    rows: [],
    total: 0,
    self: null,
    refreshTick: 0,
    canvasScale: getCanvasScale(),
    hoveredPrize: null,
    inspectedPrize: null,
  };

  // Hover + Space inspect, same mechanics as the Arcane Trials reward
  // track: Space toggles the inspector for the hovered prize card,
  // hold-to-peek closes on release, and pressing Space with nothing
  // hovered arms the next hover to open.
  inspectorHold = new InspectorHoldTracker();

  handleInspectPrize = (entry) => {
    playUI(UI.SELECT);
    this.setState({ inspectedPrize: entry });
  };

  handleHoverPrize = (entry) => {
    this.setState({ hoveredPrize: entry });
  };

  handleClosePrizeInspect = () => {
    this.setState({ inspectedPrize: null });
  };

  handlePrizeKeyDown = (e) => {
    if ((e.key === ' ' || e.code === 'Space') && !e.target?.isContentEditable && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName)) {
      e.preventDefault();
      // OS key-repeat fires while Space is held. Only act on the
      // first keydown so hold-to-inspect stays open until keyup.
      if (e.repeat) return;
      if (this.state.inspectedPrize) {
        this.inspectorHold.noteClose();
        this.setState({ inspectedPrize: null });
      } else if (this.state.hoveredPrize) {
        this.inspectorHold.noteOpen();
        this.setState({ inspectedPrize: this.state.hoveredPrize });
      } else {
        this.inspectorHold.noteArmed();
      }
    }
  };

  handlePrizeKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedPrize) return;
    e.preventDefault();
    this.setState({ inspectedPrize: null });
  };

  componentDidUpdate(_prevProps, prevState) {
    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedPrize &&
      this.state.hoveredPrize &&
      this.state.hoveredPrize !== prevState.hoveredPrize &&
      this.inspectorHold.consumePending()
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inspectedPrize: this.state.hoveredPrize });
    }

    const inspectorOpen = this.state.inspectedPrize != null;
    const wasInspectorOpen = prevState.inspectedPrize != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._prizeEscId = pushDismiss(() => this.setState({ inspectedPrize: null }));
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._prizeEscId);
    }
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handlePrizeKeyDown);
    document.addEventListener('keyup', this.handlePrizeKeyUp);
    this.fetchAll();
    // Re-tick the countdown every minute. Cheap. Only the header time
    // remaining label re-renders.
    this._countdownTimer = setInterval(() => {
      if (this._unmounted) return;
      this.setState((s) => ({ refreshTick: s.refreshTick + 1 }));
    }, 60 * 1000);
    // Keep the VirtualList itemHeight in lockstep with the renderer's
    // canvas scale so rows track the rest of the UI when the user
    // resizes the window or bumps the UI-scale slider.
    this._unsubCanvasScale = onCanvasScaleChange((scale) => {
      if (this._unmounted) return;
      this.setState({ canvasScale: scale });
    });
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._countdownTimer) clearInterval(this._countdownTimer);
    if (this._unsubCanvasScale) this._unsubCanvasScale();
    document.removeEventListener('keydown', this.handlePrizeKeyDown);
    document.removeEventListener('keyup', this.handlePrizeKeyUp);
    removeDismiss(this._prizeEscId);
  }

  fetchAll = async () => {
    this.setState({ loading: true, error: null });
    try {
      // Pull the Arcane Trials season alongside the ranked-season
      // calls. Trials is the single source of truth for the cycle
      // bounds (startsAt / endsAt / cycle number). Both ladders are
      // the same Arcane Trials cycle, so the countdown matches what
      // the trials screen displays. The ranked-season endpoint only
      // contributes rewards / scoring constants on top.
      const [rankedMeta, board, self, trialsResp] = await Promise.all([
        getCurrentRankedSeason().catch((err) => { console.warn('[SeasonRanking] meta fetch failed:', err); return null; }),
        getRankedSeasonLeaderboard({ offset: 0, limit: PAGE_SIZE }),
        getRankedSeasonSelf(),
        loadCurrentSeason().catch((err) => { console.warn('[SeasonRanking] trials season fetch failed:', err); return null; }),
      ]);
      if (this._unmounted) return;

      const season = buildMergedSeason(rankedMeta, trialsResp);
      this.setState({
        season,
        rows: board.rows,
        total: board.total,
        self,
        loading: false,
      });
    } catch (err) {
      console.error('[SeasonRankingScreen] load failed:', err);
      if (this._unmounted) return;
      this.setState({ error: t('Failed to load Ranked Season.'), loading: false });
    }
  };

  getTimeRemaining() {
    const { season } = this.state;
    if (!season?.endsAt) return null;
    const ms = new Date(season.endsAt).getTime() - Date.now();
    if (ms <= 0) return { expired: true, label: t('Rolling over...') };
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    if (days >= 1) return { expired: false, label: t('{d}d {h}h remaining', { d: days, h: hours }) };
    if (hours >= 1) return { expired: false, label: t('{h}h {m}m remaining', { h: hours, m: minutes }) };
    return { expired: false, label: t('{m}m remaining', { m: minutes }) };
  }

  render() {
    const { onClose } = this.props;
    const { loading, error, season, rows, total, self } = this.state;
    const remaining = this.getTimeRemaining();

    return (
      <div
        className="fixed inset-0 z-[100] flex flex-col"
        style={{ background: `url("/tex-noise.webp"), ${BG_ATMOSPHERE}` }}
      >
        <HeadingWatermark />
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative flex-1 flex flex-col min-h-0">
          <div className="relative shrink-0 flex items-center justify-center pt-10 pb-4 px-12">
            <div className="relative flex items-center justify-center" aria-hidden="true">
              <div className="flex items-center gap-8">
                <KnotOrnament side="left" size={32} />
                <div className="h-px w-24" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.5) 100%)` }} />
                <h1
                  className="arena-heading font-bold"
                  style={{
                    color: TEXT_PRIMARY,
                    fontSize: '2.25cqw',
                    textShadow: '0 3px 8px rgba(0,0,0,0.8), 0 0 22px rgba(212,168,67,0.3)',
                  }}
                >
                  {t('Ranked Season')}
                </h1>
                <div className="h-px w-24" style={{ background: `linear-gradient(90deg, ${GOLD} 0.5) 0%, transparent 100%)` }} />
                <KnotOrnament side="right" size={32} />
              </div>
            </div>
            <div className="absolute right-12 top-10">
              <CloseButton title={t('Close')} onClick={onClose} />
            </div>
            {season?.seasonId != null ? (
              <div
                className="absolute left-12 top-10 flex flex-col text-[0.625cqw] font-semibold"
                style={{ color: TEXT_MUTED }}
              >
                <span>{t('Season {n}', { n: season.seasonId })}</span>
                {remaining ? (
                  <span style={{ color: remaining.expired ? '#f4e4b8' : ACCENT_GOLD }}>
                    {remaining.label}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Height-constrained body: nothing here ever scrolls past the
              viewport. The leaderboard panel grows to fill the slack and
              owns its own internal scroll for the rows. */}
          {/* Capped content column: four columns of ladder plus the hero
              band never need the full canvas width, and letting them
              stretch reads as an empty table. Hero band and ladder share
              the cap so their edges stay aligned. */}
          <div className="flex-1 min-h-0 flex flex-col px-12 pb-8 w-full mx-auto" style={{ maxWidth: '84cqw' }}>
            {error ? (
              <div className="text-center py-12 text-[0.875cqw]" style={{ color: '#f87171' }}>
                {error}
              </div>
            ) : (
              // Render the full layout during loading too. The top
              // panels gracefully handle null self / season, and the
              // leaderboard shows skeleton rows. This keeps the layout
              // from popping on first paint.
              // Banded layout: hero band (your standing on the left,
              // the prize altar on the right) over the full-width
              // ladder. The hero band separates by spacing and
              // hairline rules; the ladder sits in a sheer glass
              // panel so the heading watermark frosts through it.
              <div className="flex flex-col flex-1 min-h-0 w-full">
                <div className="shrink-0 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,2.4fr)] gap-10 items-stretch px-2 pb-6">
                  <SelfScorePanel self={self} season={season} />
                  <div
                    aria-hidden="true"
                    className="w-px self-stretch my-3"
                    style={{ background: `linear-gradient(180deg, transparent 0%, ${GOLD} 0.3) 50%, transparent 100%)` }}
                  />
                  <RewardPanel
                    season={season}
                    sorceryCards={this.props.sorceryCards}
                    onInspectPrize={this.handleInspectPrize}
                    onHoverPrize={this.handleHoverPrize}
                  />
                </div>

                <LeaderboardBody
                  rows={rows}
                  total={total}
                  self={self}
                  loading={loading}
                  canvasScale={this.state.canvasScale}
                  sorceryCards={this.props.sorceryCards}
                  onViewProfile={this.props.onViewProfile}
                />
              </div>
            )}
          </div>
        </div>

        {this.state.inspectedPrize ? (
          <CardInspector
            card={this.state.inspectedPrize.card}
            imageUrl={this.state.inspectedPrize.printing?.image_url}
            rarity={this.state.inspectedPrize.card?.rarity}
            foiling={this.state.inspectedPrize.printing?.foiling}
            onClose={this.handleClosePrizeInspect}
          />
        ) : null}
      </div>
    );
  }
}

function SelfScorePanel({ self, season }) {
  const points = self?.points ?? 0;
  const matches = self?.matchesPlayed ?? 0;
  const wins = self?.wins ?? 0;
  const losses = self?.losses ?? 0;
  const eligible = !!self?.eligible;
  const rank = self?.rank ?? null;
  const matchesUntilEligible = self?.matchesUntilEligible ?? (season?.minMatches ?? 10);
  return (
    <div className="flex flex-col justify-between gap-3 py-1 min-w-0">
      <SectionHeading>{t('Your Season')}</SectionHeading>
      <div className="flex items-end gap-6">
        <div className="flex flex-col gap-1">
          <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('Valor')}</span>
          <span
            className="font-bold tabular-nums arena-heading"
            style={{
              fontSize: '3cqw',
              lineHeight: 0.9,
              color: points >= 0 ? ACCENT_GOLD : '#f87171',
              textShadow: '0 3px 0 rgba(0,0,0,0.55), 0 0 26px rgba(212,168,67,0.3), 0 0 1px rgba(255,235,180,0.4)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {points > 0 ? `+${points}` : points}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('Rank')}</span>
          <span
            className="text-[1.625cqw] font-bold tabular-nums"
            style={{
              lineHeight: 1,
              color: eligible ? TEXT_PRIMARY : TEXT_MUTED,
              textShadow: '0 2px 0 rgba(0,0,0,0.55), 0 0 12px rgba(0,0,0,0.4)',
            }}
          >
            {eligible && rank != null ? `#${rank.toLocaleString()}` : EM_DASH}
          </span>
        </div>
      </div>
      <div className="flex items-baseline gap-6 text-[0.8125cqw]" style={{ color: TEXT_BODY }}>
        <span className="flex flex-col leading-tight gap-0.5"><span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('Matches')}</span><span className="font-semibold tabular-nums">{matches}</span></span>
        <span className="flex flex-col leading-tight gap-0.5"><span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('Wins')}</span><span className="font-semibold tabular-nums" style={{ color: '#6ee7a0' }}>{wins}</span></span>
        <span className="flex flex-col leading-tight gap-0.5"><span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{t('Losses')}</span><span className="font-semibold tabular-nums" style={{ color: '#f4e4b8' }}>{losses}</span></span>
      </div>
      {!eligible ? (
        <EligibilityProgress matches={matches} threshold={(season?.minMatches ?? 10)} remaining={matchesUntilEligible} />
      ) : null}
    </div>
  );
}

// Progress bar shown while the player is still working toward the
// match threshold required to appear on the leaderboard. The track is
// a recessed dark slot with a warm gold fill. Visually consistent
// with the season-pass XP track elsewhere in the app. The label below
// reads in the same engraved style as the rest of the panel.
function EligibilityProgress({ matches, threshold, remaining }) {
  const pct = Math.max(0, Math.min(100, Math.round((matches / threshold) * 100)));
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-[0.5625cqw] font-semibold">
        <span style={{ color: TEXT_MUTED }}>{t('Qualification')}</span>
        <span className="tabular-nums" style={{ color: ACCENT_GOLD }}>
          {matches} / {threshold}
        </span>
      </div>
      <div
        className="relative h-[0.5cqw] overflow-hidden"
        style={{
          background: 'rgba(0,0,0,0.55)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(180,140,60,0.18)',
        }}
      >
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(180deg, rgba(255,230,160,0.85) 0%, rgba(212,168,67,0.95) 50%, rgba(150,110,40,0.9) 100%)`,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 8px rgba(212,168,67,0.45)',
            borderRadius: 'var(--radius-md)',
            transition: 'width 0.4s ease',
          }}
        />
      </div>
      <div className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
        {remaining === 1 ? t('1 more match to qualify') : t('{n} more matches to qualify', { n: remaining })}
      </div>
    </div>
  );
}

// Small reusable engraved heading for the in-panel section labels. The
// double text-shadow simulates an engraved-into-stone effect (warm
// highlight pushed down, dark recess pushed up) that matches the
// stone tablet panel below it.
function SectionHeading({ children }) {
  return (
    <span
      className="text-[0.6875cqw] font-bold arena-heading"
      style={{
        color: `${GOLD} 0.7)`,
        textShadow: '0 1px 0 rgba(255,235,195,0.08), 0 -1px 0 rgba(0,0,0,0.7), 0 0 14px rgba(180,140,60,0.18)',
      }}
    >
      {children}
    </span>
  );
}

// Season rewards, one tier per column, ascending prestige left to
// right. Each tier lists its coin payout and its cumulative foil prize
// cards: Top 4-10 earn the season's first prize slot, Third the first
// two, Runner-Up three, Champion all four. Card thumbs open the full
// CardInspector (foil treatment included). The portrait-pack voucher
// goes to every Top 10 finisher, so it lives as one shared footer line
// instead of repeating in each tier.
const REWARD_TIERS = [
  { rank: 4, label: k('Top 4-10'), prizeCount: 1, coinIdx: null },
  { rank: 3, label: k('Third'), prizeCount: 2, coinIdx: 2 },
  { rank: 2, label: k('Runner-Up'), prizeCount: 3, coinIdx: 1 },
  { rank: 1, label: k('Champion'), prizeCount: 4, coinIdx: 0 },
];

function resolvePrizeEntry(prize, sorceryCards) {
  const card = sorceryCards?.find((c) => c.unique_id === prize.cardId);
  const printing = resolvePrinting(card, prize.printingId, 'F');
  return { prize, card, printing };
}

function TierMedal({ rank, elevated }) {
  const medal = RANK_MEDAL_COLORS[rank];
  const size = elevated ? '1.375cqw' : '1.125cqw';
  if (!medal) {
    // Ranks 4-10 share a plain iron badge. No medal metal.
    return (
      <div
        className="shrink-0 flex items-center justify-center font-bold"
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 28%, #6b6b6b 0%, #4a4a4a 55%, #2e2e2e 100%)',
          boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.25), inset 0 -1px 2px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.6)',
          color: 'rgba(20,20,20,0.9)',
          fontSize: '0.5625cqw',
        }}
      >
        4+
      </div>
    );
  }
  const [light, mid, dark] = medal;
  return (
    <div
      className="shrink-0 flex items-center justify-center font-bold"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle at 35% 28%, ${light} 0%, ${mid} 55%, ${dark} 100%)`,
        boxShadow: [
          'inset 0 1px 1px rgba(255,255,255,0.45)',
          'inset 0 -1px 2px rgba(0,0,0,0.4)',
          `0 0 ${elevated ? 16 : 10}px rgba(212,168,67,${elevated ? 0.55 : 0.22})`,
          '0 2px 4px rgba(0,0,0,0.6)',
          `inset 0 0 0 1px ${dark}`,
        ].join(', '),
        color: 'rgba(30,22,8,0.9)',
        fontSize: elevated ? '0.6875cqw' : '0.5625cqw',
        textShadow: '0 1px 0 rgba(255,255,255,0.35)',
      }}
    >
      {rank}
    </div>
  );
}

// The prize altar. One column per prize slot, ascending toward the
// Champion card: each card belongs to the tier that unlocks it, and a
// rank claims its card plus every card to its left. Cards scale up
// with the tier so the eye reads the climb; each sits over a
// museum-plaque label (hairline, medal, coins). Click opens the full
// CardInspector with the foil treatment.
const ALTAR_CARD_WIDTHS = ['5cqw', '5.625cqw', '6.375cqw', '7.5cqw'];

function RewardPanel({ season, sorceryCards, onInspectPrize, onHoverPrize }) {
  const coins = season?.rewards?.coins ?? [0, 0, 0];
  const minPoints = season?.scoring?.minPoints ?? 1;
  const maxPoints = season?.scoring?.maxPoints ?? 40;
  const prizes = (season?.prizes || []).map((p) => resolvePrizeEntry(p, sorceryCards));
  const [earnBefore, earnAfter] = t('Earn {range} valor / match').split('{range}');
  const [voucherBefore, voucherAfter] = t('All Top 10 finishers receive a free {voucher}').split('{voucher}');
  return (
    <div className="flex flex-col gap-2 py-1 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <SectionHeading>{t('Season Rewards')}</SectionHeading>
        <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
          {earnBefore}<span style={{ color: ACCENT_GOLD }}>{minPoints}-{maxPoints}</span>{earnAfter}
        </span>
      </div>
      <div className="flex items-end justify-between gap-6 pt-1">
        {REWARD_TIERS.map((tier, i) => {
          const elevated = tier.rank === 1;
          const tierCoins = tier.coinIdx != null ? (coins[tier.coinIdx] ?? 0) : 0;
          const entry = prizes[i];
          if (!entry) return null;
          const { prize, card, printing } = entry;
          // Sites hang on the altar horizontally, like they play. Widening the
          // slot by 88/63 keeps their long edge equal to the portrait cards'
          // height, so a Site prize reads as the same size card, just turned.
          const horizontal = !!card?.played_horizontally;
          return (
            <div key={tier.rank} className="flex flex-col items-center flex-1 min-w-0">
              <div
                className="cursor-pointer"
                style={{
                  width: horizontal ? `calc(${ALTAR_CARD_WIDTHS[i]} * 88 / 63)` : ALTAR_CARD_WIDTHS[i],
                  // drop-shadow (not box-shadow): it traces the rendered
                  // silhouette of the tile, so the glow follows the
                  // horizontal site instead of leaving a portrait
                  // rectangle behind the card.
                  filter: elevated
                    ? 'drop-shadow(0 0 14px rgba(212,168,67,0.45)) drop-shadow(0 6px 10px rgba(0,0,0,0.6))'
                    : 'drop-shadow(0 4px 8px rgba(0,0,0,0.55))',
                }}
                title={prize.printingId ? t('{name}, foil, alternate art', { name: prize.name }) : t('{name}, foil', { name: prize.name })}
              >
                {card && printing ? (
                  <DeckCardTile
                    entry={{ card, printing, zone: 'spellbook', entryIndex: 0 }}
                    isSelected={false}
                    liveFoil
                    siteHorizontal
                    onClick={() => onInspectPrize?.({ card, printing })}
                    onHoverChange={(hovered) => onHoverPrize?.(hovered ? { card, printing } : null)}
                  />
                ) : (
                  <div className="w-full aspect-[63/88] rounded-card" style={{ background: '#141008' }} />
                )}
              </div>
              <div
                aria-hidden="true"
                className="w-full h-px mt-2.5"
                style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} ${elevated ? '0.6' : '0.35'}) 50%, transparent 100%)` }}
              />
              <div className="flex items-center gap-1.5 mt-1.5">
                <TierMedal rank={tier.rank} elevated={elevated} />
                <span
                  className="text-[0.5625cqw] font-semibold leading-none"
                  style={{ color: elevated ? ACCENT_GOLD : TEXT_BODY }}
                >
                  {t(tier.label)}
                </span>
                {tierCoins > 0 ? (
                  <span
                    className="flex items-center gap-0.5 font-bold tabular-nums leading-none"
                    style={{ fontSize: '0.625cqw', color: ACCENT_GOLD, textShadow: '0 1px 0 rgba(0,0,0,0.65)' }}
                  >
                    <CoinIcon size={10} />
                    {tierCoins.toLocaleString()}
                  </span>
                ) : null}
              </div>
              <span className="text-[0.5625cqw] truncate max-w-full mt-1" style={{ color: TEXT_MUTED }} title={prize.name}>
                {prize.quantity > 1 ? <span className="tabular-nums" style={{ color: ACCENT_GOLD }}>{prize.quantity}× </span> : null}
                {prize.name}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-2 text-[0.5625cqw] mt-1" style={{ color: TEXT_MUTED }}>
        <span>{t('Each rank claims its card and every card before it')}</span>
        <span aria-hidden="true" style={{ color: `${GOLD} 0.5)` }}>·</span>
        <span>{voucherBefore}<span style={{ color: ACCENT_GOLD }}>{t('portrait pack voucher')}</span>{voucherAfter}</span>
      </div>
    </div>
  );
}

// Shared row geometry. The VirtualList needs a fixed pixel height
// per item; converting cqw-based padding to a single canvas-scaled
// pixel value lets it render thousands of rows at sub-millisecond
// per-frame cost. 44px at scale 1 maps to ~2.75cqw. Comfortable
// without making the medal rows feel cramped.
const ROW_HEIGHT_PX = 60;

class LeaderboardBody extends Component {
  state = {
    search: '',
    // PlayerTitle resolves crown titles asynchronously against the
    // Hall of Fame snapshot. Each row receives an onResolve callback
    // that flips its entry in this Set on/off. The row reads back
    // its own membership to decide whether to render the trailing
    // comma on the player name. Mirrors the same mechanism the
    // ArenaHub all-time leaderboard uses, so the two ladders behave
    // identically when titles flicker in.
    idsWithTitle: new Set(),
  };
  _virtualListRef = createRef();

  setSearch = (e) => {
    this.setState({ search: e.target.value });
  };

  setHasTitle = (playerId, has) => {
    this.setState((s) => {
      const had = s.idsWithTitle.has(playerId);
      if (had === has) return null;
      const next = new Set(s.idsWithTitle);
      if (has) next.add(playerId); else next.delete(playerId);
      return { idsWithTitle: next };
    });
  };

  // Find Me: clear any active search filter (in case the user is
  // filtered out of view by it), then ask the VirtualList to scroll
  // the user's row to the centre of the viewport. Same mechanism
  // ArenaHub's all-time leaderboard uses for "Jump to my rank".
  handleFindMe = () => {
    const targetRank = this.props.self?.rank;
    if (targetRank == null) return;
    this.setState({ search: '' }, () => {
      const list = this._virtualListRef.current;
      if (list) list.scrollToIndex(targetRank - 1, { center: true });
    });
  };

  getFilteredRows() {
    const { rows } = this.props;
    const q = this.state.search.trim().toLowerCase();
    if (!q) return rows.map((row, i) => ({ row, rank: i + 1 }));
    return rows
      .map((row, i) => ({ row, rank: i + 1 }))
      .filter(({ row }) => (row.name || '').toLowerCase().includes(q));
  }

  render() {
    const { rows, total, self, loading, canvasScale, sorceryCards, onViewProfile } = this.props;
    const { search, idsWithTitle } = this.state;
    const filtered = this.getFilteredRows();
    const showFindMe = !!(self?.eligible && self?.rank != null);
    const itemHeight = Math.round(ROW_HEIGHT_PX * (canvasScale || 1));

    // Initial-load skeleton state. Show the toolbar + headers + a
    // stack of skeleton rows at the same item height the VirtualList
    // will use, so the panel doesn't reflow when real data arrives.
    const isSkeleton = loading;

    if (!isSkeleton && rows.length === 0) {
      return (
        <div
          className="relative text-center py-12 text-[0.875cqw] flex-1 min-h-0 flex items-center justify-center"
          style={GLASS_PANEL_SHEER_STYLE}
        >
          <span style={{ color: TEXT_MUTED, maxWidth: '40cqw' }}>
            {t('No players have qualified yet this season. Be the first to break the threshold.')}
          </span>
        </div>
      );
    }

    return (
      <div className="relative flex flex-col flex-1 min-h-0">
        <div
          className="relative flex flex-col overflow-hidden flex-1 min-h-0"
          style={GLASS_PANEL_SHEER_STYLE}
        >
          {/* Toolbar: bounded-width search + Find Me. The 0.375cqw inset
              plus a 0.5cqw control radius keeps the pair concentric with
              the panel's 0.875cqw shell (outer = inner + gap). Search
              input and Find Me share the radius so they read as a
              matched pair, not two arbitrary chrome bits. */}
          <div className="flex items-center justify-between gap-2 p-1.5 shrink-0">
            <input
              type="text"
              value={search}
              placeholder={t('Search players...')}
              onInput={this.setSearch}
              disabled={isSkeleton}
              className="px-3 py-1.5 text-[0.75cqw]"
              style={{
                ...INPUT_STYLE,
                borderRadius: 'var(--radius-lg)',
                width: '18cqw',
                maxWidth: '40%',
              }}
            />
            {showFindMe ? (
              <button
                type="button"
                onClick={this.handleFindMe}
                title={t('You are #{rank}', { rank: self.rank.toLocaleString() })}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 transition-all cursor-pointer"
                style={{
                  background: 'rgba(0,0,0,0.35)',
                  border: `1px solid ${GOLD} 0.3)`,
                  borderRadius: 'var(--radius-lg)',
                  color: ACCENT_GOLD,
                  fontSize: '0.625cqw',
                  fontWeight: 700,
                }}
 onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.6)`; e.currentTarget.style.background = `${GOLD} 0.1)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.3)`; e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }} aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="12" cy="12" r="1" fill="currentColor" />
                </svg>
                <span>{t('Find Me · #{rank}', { rank: self.rank.toLocaleString() })}</span>
              </button>
            ) : null}
          </div>

          {/* Column headers */}
          <div
            className="grid items-center px-4 py-2 text-[0.625cqw] font-semibold shrink-0"
            style={{
              gridTemplateColumns: '3.5cqw 1fr auto 5cqw',
              color: `${GOLD} 0.55)`,
              textShadow: '0 1px 0 rgba(0,0,0,0.6)',
            }}
          >
            <span>{t('Rank')}</span>
            <span>{t('Player')}</span>
            <span className="pr-4">{t('Record')}</span>
            <span className="text-right">{t('Valor')}</span>
          </div>

          {/* Scrollable rows. VirtualList only mounts the rows
              currently in (or near) the viewport so the panel stays
              smooth at 10k+ entries. While loading we stack skeleton
              rows at the same item height so the layout doesn't jump
              when real data lands. */}
          {isSkeleton ? (
            <div className="flex-1 min-h-0 overflow-hidden">
              {Array.from({ length: 14 }).map((_, i) => (
                <div key={`skel-${i}`} style={{ height: itemHeight }}>
                  <SeasonLeaderboardSkeletonRow rank={i + 1} variant={i < 3 ? 'rank' : 'pip'} />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex-1 min-h-0 text-center py-6 text-[0.75cqw]" style={{ color: TEXT_MUTED }}>
              {t('No matches for "{query}".', { query: search })}
            </div>
          ) : (
            <VirtualList
              ref={this._virtualListRef}
              className="flex-1 min-h-0"
              items={filtered}
              itemHeight={itemHeight}
              overscan={6}
              keyFor={(entry, i) => entry?.row?.playerId || `placeholder-${i}`}
              renderItem={(entry, i) => {
                if (!entry) {
                  return <SeasonLeaderboardSkeletonRow rank={i + 1} variant="rank" />;
                }
                const { row, rank } = entry;
                return (
                  <LeaderboardRow
                    row={row}
                    rank={rank}
                    isLastRow={i === filtered.length - 1}
                    isSelf={self?.eligible && self?.rank === rank}
                    hasTitle={idsWithTitle.has(row.playerId)}
                    sorceryCards={sorceryCards}
                    onResolveTitle={this.setHasTitle}
                    onViewProfile={onViewProfile}
                  />
                );
              }}
            />
          )}

          {total > rows.length ? (
            <div
              className="px-4 py-1.5 text-[0.5625cqw] text-center shrink-0"
              style={{
                background: 'rgba(0,0,0,0.35)',
                color: TEXT_MUTED,
                borderTop: `1px solid ${GOLD} 0.18)`,
                boxShadow: 'inset 0 1px 0 rgba(0,0,0,0.4)',
              }}
            >
              {t('Showing top {shown} of {total}', { shown: rows.length.toLocaleString(), total: total.toLocaleString() })}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}

// Skeleton row used both for the initial-load stack and as the
// renderItem fallback when VirtualList hands us an `undefined` item
// (lazy-loaded pagination slots). Mirrors LeaderboardRow's grid so
// the layout doesn't jump when real data arrives. Uses the shared
// `skeleton-block` CSS shimmer defined in index.css.
function SeasonLeaderboardSkeletonRow({ rank = null, variant = 'rank' }) {
  // Seeded pseudo-random widths so a stack of skeletons reads like a
  // ladder of varying-length player names instead of identical bars.
  const seed = rank ?? 0;
  const nameWidth = 32 + (seed * 13) % 28;
  const subWidth = 16 + (seed * 7) % 16;
  const showMedal = variant === 'rank' && rank != null && rank <= 3;
  return (
    <div
      className="relative grid items-center pl-5 pr-4 h-full"
      style={{ gridTemplateColumns: '3.5cqw 1fr auto 5cqw' }}
      aria-hidden="true"
    >
      <span
        className="absolute left-0 top-1.5 bottom-1.5 rounded-r-full"
        style={{ width: '0.1875cqw', background: 'rgba(166,160,155,0.10)' }}
      />
      {showMedal ? (
        <div
          className="skeleton-block shrink-0"
          style={{ width: '2.75cqw', height: '2.75cqw', borderRadius: '50%' }}
        />
      ) : (
        <span className="tabular-nums font-bold text-[0.875cqw]" style={{ color: `${GOLD} 0.18)` }}>
          {rank != null ? `#${rank}` : ''}
        </span>
      )}
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="skeleton-block skeleton-circle shrink-0"
          style={{ width: '2.75cqw', height: '2.75cqw' }}
        />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="skeleton-block" style={{ height: '0.75cqw', width: `${nameWidth}%` }} />
          <div className="skeleton-block" style={{ height: '0.5cqw', width: `${subWidth}%` }} />
        </div>
      </div>
      <div className="skeleton-block shrink-0 mr-4" style={{ height: '0.75cqw', width: '4.5cqw' }} />
      <div className="skeleton-block shrink-0 ml-auto" style={{ height: '0.875cqw', width: '3cqw' }} />
    </div>
  );
}

function LeaderboardRow({ row, rank, isSelf, isLastRow, hasTitle, sorceryCards, onResolveTitle, onViewProfile }) {
  const medal = RANK_MEDAL_COLORS[rank];
  const winRate = row.matchesPlayed > 0 ? Math.round((row.wins / row.matchesPlayed) * 100) : 0;
  const wrColor = winRate >= 50 ? '#6ee7a0' : '#f4e4b8';
  const avatarUrl = resolveAvatarUrl(row, sorceryCards);
  const tierColor = TIER_HEX[row.tier] || 'rgba(166,160,155,0.4)';
  const podiumTint = TOP_ROW_TINT[rank];
  // Top-3 rows render slightly taller and with their medal-tinted
  // gradient wash so the podium reads at a glance. Lower ranks share
  // a quieter baseline row style.
  const baseBg = isSelf
    ? `${GOLD} 0.10)`
    : podiumTint || 'transparent';
  const hoverBg = isSelf
    ? `${GOLD} 0.14)`
    : (podiumTint
        ? `linear-gradient(90deg, ${GOLD} 0.06) 0%, ${GOLD} 0.02) 100%), ${podiumTint}`
        : `${GOLD} 0.05)`);
  return (
    <button
      type="button"
      data-rank={rank}
      onClick={() => {
        if (onViewProfile) {
          playUI(UI.CLICK);
          onViewProfile(row.playerId);
        }
      }}
      className="relative grid items-center pl-5 pr-4 text-left cursor-pointer w-full h-full"
      style={{
        gridTemplateColumns: '3.5cqw 1fr auto 5cqw',
        background: baseBg,
        borderBottom: isLastRow ? 'none' : `1px solid rgba(0,0,0,0.35)`,
        boxShadow: isLastRow ? undefined : 'inset 0 -1px 0 rgba(255,235,195,0.04)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = baseBg; }}
    >
      {/* Tier-tinted left stripe. The only per-row color signal that
          survives scrolling past the medal rows. Reuses the same
          palette as ArenaHub's all-time leaderboard. */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-1.5 bottom-1.5 rounded-r-full"
        style={{
          width: '0.1875cqw',
          background: `linear-gradient(180deg, ${tierColor}88 0%, ${tierColor}33 100%)`,
          boxShadow: `0 0 6px ${tierColor}44`,
        }}
      />
      {medal ? (
        <div
          className="flex items-center justify-center font-bold"
          style={{
            width: '2.75cqw',
            height: '2.75cqw',
            borderRadius: '50%',
            background: `radial-gradient(circle at 35% 30%, ${medal[0]} 0%, ${medal[1]} 55%, ${medal[2]} 100%)`,
            boxShadow: [
              'inset 0 1px 1px rgba(255,255,255,0.4)',
              'inset 0 -1px 2px rgba(0,0,0,0.4)',
              `0 0 14px rgba(212,168,67,${rank === 1 ? 0.55 : 0.22})`,
              '0 2px 4px rgba(0,0,0,0.6)',
              `inset 0 0 0 1px ${medal[2]}`,
            ].join(', '),
            color: 'rgba(30,22,8,0.9)',
            fontSize: '1.125cqw',
            textShadow: '0 1px 0 rgba(255,255,255,0.3)',
          }}
        >
          {rank}
        </div>
      ) : (
        <span
          className="tabular-nums font-bold text-[0.875cqw]"
          style={{
            color: `${GOLD} 0.4)`,
            textShadow: '0 1px 0 rgba(0,0,0,0.6)',
          }}
        >
          #{rank}
        </span>
      )}
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="relative shrink-0 rounded-full"
          style={{
            width: '2.75cqw',
            height: '2.75cqw',
            background: 'rgba(0,0,0,0.45)',
            border: `1px solid ${tierColor}66`,
            boxShadow: `0 0 8px ${tierColor}22, inset 0 0 0 1px rgba(0,0,0,0.4)`,
            overflow: 'hidden',
          }}
        >
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} className="w-full h-full" />
          ) : null}
        </div>
        {/* Name + title column. justify-center collapses any internal
            whitespace so the two-line block centers as a unit on the
            avatar's vertical midpoint regardless of whether the title
            has resolved yet. The avatar always sits on the same
            visual axis as the text. leading-tight on the name keeps
            both lines compact so the negative margin pulling the
            title up under the name reads as one wrapped phrase. */}
        <div className="flex flex-col justify-center min-w-0 leading-tight">
          <span
            className="truncate text-[0.875cqw] font-semibold"
            style={{ color: TEXT_PRIMARY, textShadow: '0 1px 0 rgba(0,0,0,0.5)' }}
          >
            {row.name}{hasTitle ? ',' : ''}
            {row.discordHandle ? (
              <span className="ml-2 inline-block align-middle">
                <DiscordHandlePill handle={row.discordHandle} />
              </span>
            ) : null}
          </span>
          {/* Crown title under the name, rendered in the same colour
              as the name so the two read as a single line wrapped to
              two. PlayerTitle resolves asynchronously against the
              Hall of Fame; the onResolve callback bubbles up so the
              parent can toggle the trailing comma on the name above. */}
          <PlayerTitle
            player={{
              id: row.playerId,
              displayedCrownAvatarId: row.displayedCrownAvatarId,
              isFounder: row.isFounder,
              isQaTester: row.isQaTester,
            }}
            sorceryCards={sorceryCards}
            prefix=""
            style={{
              display: 'block',
              fontSize: '0.625cqw',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: '0.1cqw',
              color: TEXT_PRIMARY,
              opacity: 0.85,
            }}
            onResolve={(has) => onResolveTitle?.(row.playerId, has)}
          />
        </div>
        {/* Badges the player pinned to their profile. The same set the
            profile header strip shows, so a ladder row carries the same
            emblems as the profile it links to. */}
        <PlayerBadgeStrip
          player={{
            id: row.playerId,
            isFounder: row.isFounder,
            isQaTester: row.isQaTester,
            pinnedBadgeIds: row.pinnedBadgeIds,
          }}
          sorceryCards={sorceryCards}
          size="1.5cqw"
        />
      </div>
      <div className="flex items-baseline gap-3 pr-4 text-[0.75cqw]" style={{ color: TEXT_BODY }}>
        <span><span style={{ color: TEXT_MUTED }}>W</span> <span className="font-semibold tabular-nums" style={{ color: '#6ee7a0' }}>{row.wins}</span></span>
        <span><span style={{ color: TEXT_MUTED }}>L</span> <span className="font-semibold tabular-nums" style={{ color: '#f4e4b8' }}>{row.losses}</span></span>
        <span className="font-semibold tabular-nums" style={{ color: wrColor }}>{winRate}%</span>
      </div>
      <span
        className="text-right font-bold tabular-nums text-[1.0625cqw]"
        style={{
          color: row.points >= 0 ? ACCENT_GOLD : '#f87171',
          textShadow: '0 1px 0 rgba(0,0,0,0.7), 0 0 12px rgba(212,168,67,0.2)',
        }}
      >
        {row.points > 0 ? `+${row.points}` : row.points}
      </span>
    </button>
  );
}
